import {
  type AcknowledgePublicationInput,
  isPendingRejectionCode,
} from './publication';

/** Why the server refused a publication for good; kept with its journal entry. */
export type PublicationRefusal =
  | 'FORBIDDEN'
  | 'REMOTE_CONFLICT'
  | 'FORMAT_MISMATCH'
  | 'LIMIT_EXCEEDED';

export const PUBLICATION_REFUSALS: readonly PublicationRefusal[] = [
  'FORBIDDEN',
  'REMOTE_CONFLICT',
  'FORMAT_MISMATCH',
  'LIMIT_EXCEEDED',
];

export interface RefusedPublication {
  readonly operationId: string;
  readonly docIds: readonly string[];
  readonly refusal: PublicationRefusal;
}

/** What the user decided for the refused publications of a set of documents. */
export type RefusedPublicationResolution = 'retry' | 'discard';

/**
 * The reason stored with a BLOCKED_CONFLICT entry: the rejection code, or
 * REMOTE_CONFLICT for a group the server persisted and then ended without
 * materializing it. null for every receipt that does not block.
 */
export function publicationRefusalFromReceipt(
  receipt: AcknowledgePublicationInput['receipt']
): PublicationRefusal | null {
  switch (receipt.state) {
    case 'rejected':
      return isPendingRejectionCode(receipt.code) ? null : receipt.code;
    case 'FAILED':
      return receipt.failureClass === 'semantic_precondition' ||
        receipt.failureClass === 'claim_loss'
        ? null
        : 'REMOTE_CONFLICT';
    case 'CANCELLED':
    case 'QUARANTINED':
      return 'REMOTE_CONFLICT';
    case 'PENDING':
    case 'CLAIMED':
    case 'COMMITTED':
      return null;
  }
}

export interface RefusedPublicationStorage {
  /**
   * BLOCKED_CONFLICT entries with at least one member in docIds, in journal
   * order. Reads no payload bytes. An entry written before refusals were
   * stored reports REMOTE_CONFLICT.
   */
  listRefusedPublications(
    docIds: readonly string[]
  ): Promise<readonly RefusedPublication[]>;
  /**
   * One local transaction, never a remote call; only on the user's request.
   *
   * retry: every BLOCKED_CONFLICT entry with a member in docIds becomes
   * REBASING and loses its refusal. Bytes, mutationId, attempt and send policy
   * stay; the regular send path publishes it as attempt+1.
   *
   * discard: every journal entry with a member in docIds is deleted, whatever
   * its state, together with the local updates, snapshots, clocks and
   * publication actors of docIds and of those entries' members. The next pull
   * restores the server state. Other documents are not touched.
   *
   * Returns the affected document ids; no matching entry => empty, no writes
   * (discard still clears the local bytes of docIds).
   */
  resolveRefusedPublications(
    docIds: readonly string[],
    resolution: RefusedPublicationResolution
  ): Promise<readonly string[]>;
}
